<template>
  <div class="klarvertrag-page">
    
    <div
      v-if="contractBreadcrumbLink"
      class="klarvertrag-breadcrumb"
    >
      <router-link :to="contractBreadcrumbLink">
        {{ t('common.labels.contractBreadcrumbLabel') }}: {{ selectedProductName }}
      </router-link>
    </div>
    <div class="page-header">
      <h1 class="page-title">
        Klarvertrag Questions
        <span
          v-if="selectedProductName"
          class="page-title-product"
        >
          · {{ selectedProductName }}
        </span>
      </h1>
      <p class="page-subtitle">
        <span v-if="selectedProductName">
          Leistungen und Antworten für {{ selectedProductName }}
        </span>
        <span v-else>
          Review contract questions and answers for your products
        </span>
      </p>
    </div>



    <!-- Product Selection -->
    <div
      v-if="klvProducts.length > 0 && !selectedProduct"
      class="product-selection-inline"
    >
      <label class="product-selection-label">
        <v-icon size="20" color="#EA5545" class="mr-2">mdi-package-variant</v-icon>
        Produkt wählen
      </label>
      <v-select
        v-model="selectedProductKlvId"
        :items="klvProductOptions"
        item-title="label"
        item-value="value"
        variant="outlined"
        density="comfortable"
        placeholder="Produkt auswählen..."
        :loading="isLoadingProducts"
        hide-details
      />
    </div>

    <!-- Brain Search -->
    <div
      v-if="selectedProduct && hasKlarvertragFeature && selectedProductSupportsBrainSearch"
      class="brain-search-section"
    >
      <BrainSearch
        :showHistory="false"
        :presetProductId="selectedMyProductId"
        :lockProductSelection="true"
        :title="selectedProductName"
        :fullWidth="true"
      />
    </div>

    <div
      v-if="selectedProduct && hasKlarvertragFeature && !canLoadCurrentKlarvertrag"
      class="info-state"
    >
      <v-icon size="56" color="#ffb300" class="mb-3">mdi-lock-alert</v-icon>
      <h3 class="info-title">Klarvertrag noch nicht verfügbar</h3>
      <p class="info-text">
        Für dieses Produkt steht der aktuelle Klarvertrag (Typ 2) noch nicht bereit.
        Sobald die Leistungsfragen freigeschaltet sind, findest du sie hier automatisch.
      </p>
    </div>

    <!-- Questions Display -->
    <Klv3Simple
      v-if="shouldRenderKlv3"
      :product="selectedProduct"
      :tarif-id="currentTarifId"
      @product-loaded="handleProductLoaded"
    />

    <Klv5Zahn
      v-else-if="shouldRenderKlv5Zahn"
      :product="selectedProduct"
      :tarif-id="currentTarifId"
      @product-loaded="handleProductLoaded"
    />

    <div v-else-if="showUnsupportedNotice" class="info-state">
      <v-icon size="56" color="#ffb300" class="mb-3">mdi-alert-circle-outline</v-icon>
      <h3 class="info-title">Dieser Klarvertrag wird aktuell nicht unterstützt</h3>
      <p class="info-text">
        Für diese Vertragskategorie steht der Klarvertrag-Typ aktuell nicht zur Verfügung.
        <span v-if="debugInfo">
          (prod_category: {{ debugInfo.prod_category }}, prod_klv_type: {{ debugInfo.prod_klv_type }},
          prod_klv_id: {{ debugInfo.prod_klv_id }}, Quelle: {{ debugInfo.source }})
        </span>
      </p>
    </div>

    <div
      v-else-if="selectedProductKlvId && !selectedProduct && !isLoadingProducts"
      class="state-block" data-state="empty"
    >
      <v-icon size="80" color="#ccc">mdi-shield-alert</v-icon>
      <h3 class="state-title">Vertrag nicht verfügbar</h3>
      <p class="state-text">
        Für diesen Klarvertrag konnte kein Vertrag in deinem Account gefunden werden.
      </p>
    </div>

    <div v-else-if="!selectedProductKlvId && klvProducts.length > 0" class="select-prompt">
      <v-icon size="80" color="#ccc">mdi-file-question</v-icon>
      <h3 class="state-title">No Questions Found</h3>
      <p class="state-text">There are no Klarvertrag questions for this product.</p>
    </div>

    <!-- No Products Available -->
    <div v-else class="no-products-state">
      <v-icon size="80" color="#ccc">mdi-package-variant-closed</v-icon>
      <h3 class="state-title">No Products with Klarvertrag</h3>
      <p class="state-text">You don't have any products with Klarvertrag questions assigned yet.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, onMounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import BrainSearch from '@/components/objects/BrainSearch.vue'
import Klv5Zahn from '@/components/sites/modules/Klv5Zahn.vue'
import Klv3Simple from '@/components/sites/modules/Klv3Simple.vue'
import { useLanguage } from '@/composables/useLanguage'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'

const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')
const route = useRoute()
const { t } = useLanguage()
const updateDocumentHead = inject('updateDocumentHead')
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)

// State
const klvProducts = ref([])
const selectedProductKlvId = ref(null)
const isLoadingProducts = ref(false)
const apiSelectedProduct = ref(null)

// Route parameter (optional tarifId)
const routeTarifId = computed(() => {
  const raw = route.params.tarifId
  if (!raw && raw !== 0) return null
  const parsed = Number(raw)
  return Number.isNaN(parsed) ? null : parsed
})

// Computed
const klvProductOptions = computed(() => {
  return klvProducts.value.map(product => ({
    label: product.prod_name,
    value: product.prod_klv_id
  }))
})

const selectedProduct = computed(() => {
  const hasSelection = selectedProductKlvId.value || selectedProductKlvId.value === 0
  if (hasSelection) {
    const targetId = Number(selectedProductKlvId.value)
    const match = klvProducts.value.find(
      product => Number(product.prod_klv_id) === targetId
    )
    if (match) {
      if (
        apiSelectedProduct.value &&
        Number(apiSelectedProduct.value.prod_klv_id) === targetId
      ) {
        return {
          ...match,
          ...apiSelectedProduct.value
        }
      }
      return match
    }
    if (
      apiSelectedProduct.value &&
      Number(apiSelectedProduct.value.prod_klv_id) === targetId
    ) {
      return apiSelectedProduct.value
    }
    return null
  }
  return apiSelectedProduct.value
})

const selectedProductName = computed(() => {
  return selectedProduct.value?.prod_name || ''
})

const selectedMyProductId = computed(() => {
  const product = selectedProduct.value
  if (!product) return null
  return product.myp_product_id ?? product.myp_id ?? null
})

const hasKlarvertragFeature = computed(() => {
  const product = selectedProduct.value
  if (!product) return false
  return Number(product.prod_klv_type || 0) > 0
})

const canLoadCurrentKlarvertrag = computed(() => {
  const product = selectedProduct.value
  if (!product) return false
  const klvType = Number(product.prod_klv_type || 0)
  return klvType === 3 || klvType === 5
})

const selectedProductSupportsBrainSearch = computed(() => {
  const product = selectedProduct.value
  if (!product) return false
  return Number(product.prod_brain_search || 0) === 1
})

const shouldRenderKlv3 = computed(() => {
  const product = selectedProduct.value
  if (!product) return false
  return Number(product.prod_klv_type || 0) === 3
})

const shouldRenderKlv5Zahn = computed(() => {
  const product = selectedProduct.value
  if (!product) return false
  const klvType = Number(product.prod_klv_type || 0)
  if (klvType !== 5) return false
  const category = product.prod_category
  if (category === undefined || category === null) {
    return true
  }
  return Number(category) === 1
})

const showUnsupportedNotice = computed(() => {
  const product = selectedProduct.value
  if (!product) return false
  const klvType = Number(product.prod_klv_type || 0)
  // Don't show for type 3 or non-5 types
  if (klvType !== 5) return false
  const category = product.prod_category
  if (category === undefined || category === null) {
    return false
  }
  return Number(category) !== 1
})

const debugInfo = computed(() => {
  const product = selectedProduct.value
  if (!product) return null
  return {
    prod_category: product.prod_category ?? '(unset)',
    prod_klv_type: product.prod_klv_type ?? '(unset)',
    prod_klv_id: product.prod_klv_id ?? '(unset)',
    source: product === apiSelectedProduct.value ? 'apiSelectedProduct' : 'klvProducts'
  }
})
const currentTarifId = computed(() => {
  if (selectedProductKlvId.value !== null && selectedProductKlvId.value !== undefined) {
    return selectedProductKlvId.value
  }
  return selectedProduct.value?.prod_klv_id ?? null
})

const contractBreadcrumbLink = computed(() => {
  if (!selectedProduct.value?.myp_id) return null
  return {
    name: 'vertrag',
    params: { id: selectedProduct.value.myp_id }
  }
})

// Functions
async function loadKlvProducts() {
  isLoadingProducts.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getmyproducts'
    })
    
    if (response.data.success && response.data.products) {
      // Filter products with Klarvertrag feature
      klvProducts.value = response.data.products.filter(
        p => Number(p.prod_klv_type || 0) > 0
      )
      // Auto-select product from route if possible
      autoSelectRouteTarifId()
    }
  } catch (err) {
    console.error('Error loading products:', err)
    errorStore.setError(true, 'Failed to load products')
  } finally {
    isLoadingProducts.value = false
  }
}

function autoSelectRouteTarifId() {
  if (routeTarifId.value === null || routeTarifId.value === undefined) return
  if (selectedProductKlvId.value === routeTarifId.value) return
  selectedProductKlvId.value = routeTarifId.value
}

function handleProductLoaded(product) {
  if (product) {
    if (
      selectedProductKlvId.value !== null &&
      Number(product.prod_klv_id ?? -1) === Number(selectedProductKlvId.value ?? -2)
    ) {
      const index = klvProducts.value.findIndex(
        p => Number(p.prod_klv_id ?? -1) === Number(product.prod_klv_id ?? -2)
      )
      if (index !== -1) {
        klvProducts.value[index] = {
          ...klvProducts.value[index],
          ...product
        }
      }
    }
    apiSelectedProduct.value = product
  }
}

watch(selectedProductKlvId, (newId) => {
  if (
    apiSelectedProduct.value &&
    Number(apiSelectedProduct.value.prod_klv_id ?? -1) !== Number(newId ?? -1)
  ) {
    apiSelectedProduct.value = null
  }
})

// Watch for route param changes (e.g. navigation between klarvertrag IDs)
watch(routeTarifId, () => {
  autoSelectRouteTarifId()
})

onMounted(() => {
  loadKlvProducts()
})
</script>

<style scoped src="@/assets/styles/sfc/components/sites/KlarvertragIndex.css"></style>
